import React, { useState, useEffect, useCallback } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { mockData } from '../mockData';
import { scrollToSection } from '../lib/scroll';

const navLinks = [
  { name: 'Início', id: 'home' },
  { name: 'Sobre Nós', id: 'about' },
  { name: 'Áreas de Atuação', id: 'services' },
  { name: 'Equipa', id: 'team' },
  { name: 'Testemunhos', id: 'testimonials' },
  { name: 'Contactos', id: 'contact' }
];

const NavbarLogo = ({ onClick }) => (
  <button onClick={onClick} className="flex items-center space-x-3 group" data-testid="navbar-logo">
    <img
      src={mockData.company.logoUrl}
      alt="Barros & Durães Advogados"
      className="w-12 h-12 object-contain"
    />
    <div className="hidden md:block">
      <div className="text-navy font-serif text-lg font-bold">Barros & Durães</div>
      <div className="text-navy/70 text-xs uppercase tracking-wider">Advogados</div>
    </div>
  </button>
);

const DesktopNav = ({ onNavigate }) => (
  <div className="hidden md:flex items-center space-x-8">
    {navLinks.map((link) => (
      <button
        key={link.id}
        onClick={() => onNavigate(link.id)}
        data-testid={`nav-link-${link.id}`}
        className="text-navy/80 hover:text-gold font-medium text-sm transition-colors duration-200 relative group"
      >
        {link.name}
        <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gold group-hover:w-full transition-all duration-300"></span>
      </button>
    ))}
    <Button
      onClick={() => onNavigate('contact')}
      data-testid="navbar-cta-button"
      className="bg-navy hover:bg-navy/90 text-white px-6 py-2 rounded-sm transition-all duration-300 hover:shadow-lg"
    >
      Agendar Consulta
    </Button>
  </div>
);

const MobileMenu = ({ onNavigate }) => (
  <div className="md:hidden bg-white border-t border-cream" data-testid="mobile-menu">
    <div className="px-4 py-6 space-y-4">
      {navLinks.map((link) => (
        <button
          key={link.id}
          onClick={() => onNavigate(link.id)}
          data-testid={`mobile-nav-link-${link.id}`}
          className="block w-full text-left text-navy/80 hover:text-gold font-medium py-2 transition-colors"
        >
          {link.name}
        </button>
      ))}
      <Button
        onClick={() => onNavigate('contact')}
        data-testid="mobile-cta-button"
        className="w-full bg-navy hover:bg-navy/90 text-white py-3 rounded-sm"
      >
        Agendar Consulta
      </Button>
    </div>
  </div>
);

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleNavigate = (sectionId) => {
    scrollToSection(sectionId);
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <NavbarLogo onClick={() => handleNavigate('home')} />
          <DesktopNav onNavigate={handleNavigate} />
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-navy"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      {isMobileMenuOpen && <MobileMenu onNavigate={handleNavigate} />}
    </nav>
  );
};

export default Navbar;
